import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  BarChart3,
  Briefcase,
  BookOpen,
  Building2,
  ChevronLeft,
  ChevronRight,
  FlaskConical,
  GraduationCap,
  Grid3X3,
  LineChart,
  Users,
  Wallet,
  X,
} from "lucide-react";

const navItems = [
  {
    label: "Projects",
    path: "/capital",
    icon: Briefcase,
    isActive: (pathname) =>
      pathname === "/capital" ||
      (pathname.startsWith("/capital/") &&
        !pathname.startsWith("/capital/planning") &&
        !pathname.startsWith("/capital/portfolio") &&
        !pathname.startsWith("/capital/budget-actual")),
  },
  {
    label: "Planning",
    path: "/capital/planning",
    icon: LineChart,
    isActive: (pathname) => pathname.startsWith("/capital/planning"),
  },
  {
    label: "Portfolio",
    path: "/capital/portfolio",
    icon: Grid3X3,
    isActive: (pathname) => pathname.startsWith("/capital/portfolio"),
  },
  {
    label: "Budget vs Actual",
    path: "/capital/budget-actual",
    icon: BarChart3,
    isActive: (pathname) => pathname.startsWith("/capital/budget-actual"),
  },
];

const MODULE_SWITCHER_ITEMS = [
  {
    key: "budget",
    label: "Budget",
    path: "/budget",
    icon: Wallet,
  },
  {
    key: "positions",
    label: "Positions",
    path: "/positions/budgeting",
    icon: Users,
  },
  {
    key: "research",
    label: "Research",
    path: "/research",
    icon: FlaskConical,
  },
  {
    key: "tuition",
    label: "Tuition",
    path: "/fees/projection",
    icon: GraduationCap,
  },
  {
    key: "reporting",
    label: "Reporting",
    path: "/superset",
    icon: BarChart3,
  },
  {
    key: "courses",
    label: "Courses",
    path: "/courses",
    icon: BookOpen,
  },
  {
    key: "capital",
    label: "Capital",
    path: "/capital/planning",
    icon: Building2,
  },
];

const CapitalSideNav = ({ isMobileOpen, onMobileClose }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const activeModuleKey = "capital";
  const switcherRef = useRef(null);
  const autoScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem("capitalSideNavCollapsed");
      if (saved === "true") {
        setCollapsed(true);
      }
    } catch (_) {
      // ignore localStorage errors
    }
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    if (isMobileOpen && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("capitalSideNavCollapsed", String(next));
      } catch (_) {
        // ignore localStorage errors
      }
      return next;
    });
  };

  const updateSwitcherButtons = useCallback(() => {
    const container = switcherRef.current;
    if (!container) return;
    // Skip if container hasn't been laid out yet
    if (container.clientWidth === 0) return;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(maxScrollLeft > 0 && container.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    if (collapsed) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return undefined;
    }
    updateSwitcherButtons();
    const container = switcherRef.current;
    if (!container) return undefined;
    const handleScroll = () => updateSwitcherButtons();
    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [collapsed, updateSwitcherButtons]);

  useLayoutEffect(() => {
    if (collapsed) return;
    updateSwitcherButtons();
  }, [collapsed, updateSwitcherButtons]);

  useEffect(() => {
    if (collapsed) return;
    // Use multiple frames to ensure layout is complete
    let frame1, frame2, timer;
    frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        updateSwitcherButtons();
      });
    });
    timer = window.setTimeout(() => updateSwitcherButtons(), 300);
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
      window.clearTimeout(timer);
    };
  }, [collapsed, updateSwitcherButtons]);

  useEffect(() => {
    if (collapsed) return;
    const container = switcherRef.current;
    if (!container || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => updateSwitcherButtons());
    observer.observe(container);
    return () => observer.disconnect();
  }, [collapsed, updateSwitcherButtons]);

  useEffect(() => {
    if (collapsed || !document?.fonts?.ready) return undefined;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) updateSwitcherButtons();
    });
    return () => {
      cancelled = true;
    };
  }, [collapsed, updateSwitcherButtons]);

  const scrollSwitcher = (direction) => {
    const container = switcherRef.current;
    if (!container) return;
    const delta = Math.max(160, Math.floor(container.clientWidth * 0.6));
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const next = Math.min(
      Math.max(container.scrollLeft + direction * delta, 0),
      maxScrollLeft,
    );
    container.scrollTo({ left: next, behavior: "smooth" });
    updateSwitcherButtons();
  };

  const stopAutoScroll = useCallback(() => {
    if (!autoScrollRef.current) return;
    window.clearInterval(autoScrollRef.current);
    autoScrollRef.current = null;
  }, []);

  const startAutoScroll = useCallback(
    (direction) => {
      if (autoScrollRef.current) return;
      const container = switcherRef.current;
      if (!container) return;
      autoScrollRef.current = window.setInterval(() => {
        const activeContainer = switcherRef.current;
        if (!activeContainer) return;
        const maxScrollLeft =
          activeContainer.scrollWidth - activeContainer.clientWidth;
        const next = Math.min(
          Math.max(activeContainer.scrollLeft + direction * 3, 0),
          maxScrollLeft,
        );
        if (next === activeContainer.scrollLeft) {
          stopAutoScroll();
          return;
        }
        activeContainer.scrollTo({ left: next, behavior: "auto" });
        updateSwitcherButtons();
      }, 16);
    },
    [stopAutoScroll, updateSwitcherButtons],
  );

  useEffect(() => {
    return () => stopAutoScroll();
  }, [stopAutoScroll]);

  // Create a function to render content so refs work properly
  const renderSidebarContent = (useRef = false) => (
    <div className={`group/sidebar relative overflow-hidden rounded-2xl border border-emerald-100/70 bg-gradient-to-br from-emerald-50 via-emerald-100 to-teal-100/70 p-4 shadow-xl dark:border-slate-700/70 dark:from-slate-900 dark:via-emerald-950 dark:to-teal-950/40 md:sticky md:top-24 md:flex md:max-h-[calc(100vh-6rem)] md:flex-col ${
      collapsed ? "md:overflow-visible" : ""
    }`}>
      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-emerald-200/40 blur-2xl dark:bg-emerald-500/10" />
      <div className="absolute -left-6 top-14 h-16 w-16 rounded-full bg-teal-200/40 blur-2xl dark:bg-teal-500/10" />
      <div className="relative md:flex md:flex-1 md:min-h-0 md:flex-col">
        <div
          className={`flex items-start justify-between gap-2 ${
            collapsed
              ? "md:flex-col md:items-center md:justify-start md:gap-3"
              : ""
          }`}
        >
          <div className="flex items-center gap-3">
            <span className="rounded-xl bg-emerald-600 p-2 text-white shadow-lg shadow-emerald-900/20">
              <Building2 className="h-5 w-5" />
            </span>
            {!collapsed && (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-700 dark:text-emerald-300">
                  Capital
                </div>
                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Module
                </div>
              </div>
            )}
          </div>
          {/* Close button for mobile */}
          {isMobileOpen && (
            <button
              type="button"
              className="md:hidden rounded-lg border border-emerald-200/70 bg-white/80 p-2 text-emerald-700 shadow-sm transition hover:bg-white dark:border-slate-700/70 dark:bg-slate-900/70 dark:text-emerald-200 touch-target"
              onClick={onMobileClose}
              aria-label="Close navigation"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          {/* Collapse button for desktop */}
          <div
            className={`relative hidden md:block ${
              collapsed
                ? "md:absolute md:-right-7 md:top-3 md:z-20 md:translate-y-1 md:opacity-0 md:pointer-events-none md:group-hover/sidebar:translate-y-0 md:group-hover/sidebar:opacity-100 md:group-hover/sidebar:pointer-events-auto md:focus-within:translate-y-0 md:focus-within:opacity-100 md:focus-within:pointer-events-auto"
                : ""
            }`}
          >
            <button
              type="button"
              className="peer rounded-lg border border-emerald-200/70 bg-white/80 p-2 text-emerald-700 shadow-sm transition hover:bg-white dark:border-slate-700/70 dark:bg-slate-900/70 dark:text-emerald-200"
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
            {collapsed && (
              <span className="pointer-events-none absolute left-1/2 top-full mt-2 z-20 hidden -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-full bg-slate-900/90 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white shadow-lg opacity-0 transition md:block peer-hover:translate-y-0 peer-hover:opacity-100 peer-focus-visible:translate-y-0 peer-focus-visible:opacity-100 dark:bg-slate-100 dark:text-slate-900">
                Expand navigation
              </span>
            )}
          </div>
        </div>
        {!collapsed && (
          <>
            <nav className="mt-3" aria-label="Switch modules">
              <div className="module-switcher relative flex items-center">
                <button
                  type="button"
                  className={`module-switcher-chevron shrink-0 ${
                    canScrollLeft ? "" : "opacity-30"
                  }`}
                  onClick={() => scrollSwitcher(-1)}
                  onMouseEnter={() => startAutoScroll(-1)}
                  onMouseLeave={stopAutoScroll}
                  aria-label="Scroll modules left"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div
                  ref={useRef ? switcherRef : null}
                  className="flex-1 min-w-0 overflow-x-auto mx-1 py-1 scrollbar-hide rounded-full border border-slate-200/70 bg-slate-50/80 dark:border-slate-700/60 dark:bg-slate-900/50"
                  style={{ maxWidth: "calc(100% - 70px)" }}
                >
                  <div className="flex w-max items-center gap-2 px-1">
                    {MODULE_SWITCHER_ITEMS.map((module) => {
                      const active = module.key === activeModuleKey;
                      const ModuleIcon = module.icon;
                      return (
                        <Link
                          key={module.key}
                          to={module.path}
                          aria-current={active ? "page" : undefined}
                          className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                            active
                              ? "border-emerald-200/70 bg-white/90 text-emerald-700 shadow-sm dark:border-emerald-900/60 dark:bg-slate-900/80 dark:text-emerald-200"
                              : "border-transparent text-slate-600 hover:bg-white/70 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800/60 dark:hover:text-slate-200"
                          }`}
                        >
                          <span
                            className={`flex h-5 w-5 items-center justify-center rounded-full ${
                              active
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
                                : "bg-white/80 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
                            }`}
                          >
                            <ModuleIcon className="h-3.5 w-3.5" />
                          </span>
                          <span className="whitespace-nowrap">{module.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  className={`module-switcher-chevron shrink-0 ${
                    canScrollRight ? "" : "opacity-30"
                  }`}
                  onClick={() => scrollSwitcher(1)}
                  onMouseEnter={() => startAutoScroll(1)}
                  onMouseLeave={stopAutoScroll}
                  aria-label="Scroll modules right"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </nav>
            <div className="mt-2 text-xs text-slate-600 dark:text-slate-300">
              Jump between capital workstreams and dashboards.
            </div>
          </>
        )}
        <div className="mt-4 md:flex-1 md:min-h-0 md:overflow-y-auto md:pr-1">
          <nav>
            <div
              className={`flex flex-col gap-2 ${
                collapsed ? "md:items-center" : ""
              }`}
            >
              {navItems.map((item) => {
                const active = item.isActive(location.pathname);
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    title={collapsed ? item.label : undefined}
                    aria-label={collapsed ? item.label : undefined}
                    className={`group relative flex rounded-xl border px-3 py-3 text-sm font-semibold transition touch-target ${
                      collapsed
                        ? "md:min-w-[56px] justify-center"
                        : "items-center gap-3"
                    } ${
                      active
                        ? "border-emerald-200 bg-white/80 text-emerald-700 shadow-sm dark:border-emerald-900/60 dark:bg-slate-900/70 dark:text-emerald-200"
                      : "border-transparent text-slate-600 hover:bg-white/70 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800/60 dark:hover:text-slate-200"
                    }`}
                  >
                    {active && (
                      <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-full bg-emerald-500" />
                    )}
                    <span
                      className={`rounded-lg p-1.5 ${
                        active
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
                        : "bg-white/80 text-slate-500 group-hover:bg-white dark:bg-slate-800 dark:text-slate-300 dark:group-hover:bg-slate-700/80 dark:group-hover:text-slate-200"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    {!collapsed && (
                      <span className="whitespace-nowrap">{item.label}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </nav>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className={`hidden md:block ${collapsed ? "md:w-20" : "md:w-64"} shrink-0`}>
        {renderSidebarContent(true)}
      </aside>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="mobile-sidebar-overlay md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] overflow-y-auto bg-white dark:bg-slate-900 p-4 shadow-2xl md:hidden safe-area-bottom ${
          isMobileOpen ? "sidebar-slide-in" : "hidden"
        }`}
      >
        {renderSidebarContent(false)}
      </aside>
    </>
  );
};

export default CapitalSideNav;
